import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { BaseService } from '../../abstract/base.service';
import { DroneResource } from '../data-models/drone-resource.model';
import { DroneResourceRequest } from '../request-models/drone-resource-request.model';
import { DroneResourceUpload } from '../request-models/drone-resource-upload.model';
import { DroneResourceUploadUrlRequest } from '../request-models/drone-resource-upload-url-request.model';
import { DroneResourceUploadTarget } from '../response-models/drone-resource-upload-target.model';
import { isRecord } from '../utils/is-record';
import { nullableString } from '../utils/wire-value-utils';
import { FileUploadService } from '../api-services/file-upload.service';

/**
 * A drone's shared documents: `/drones/:droneId/resources[/:id[/download-url]]` and `/upload-url`
 * (hub `user-drone-resources-controller.ts`), a nested endpoint under the drone.
 *
 * `adminOnly` rows are filtered server-side, and operator uploads are always registered as shared:
 * internal-only is an admin-portal concept. Every method rejects with an `ApiError` (or an `Error` for
 * a file that fails local validation).
 */
@Injectable({
  providedIn: 'root'
})
export class DroneResourceService extends BaseService<DroneResource> {
  private fileUploadService = inject(FileUploadService);

  constructor() {
    super('resources', DroneResource);
  }

  async getResourcesByDroneId(droneId: string, resourceType?: string): Promise<DroneResource[]> {
    return firstValueFrom(this.getMany({ prefix: dronePrefix(droneId), query: { resourceType } }));
  }

  /** Presign, PUT to S3, then register the row. Throws on any step so callers have a single error path. */
  async uploadResourceByDroneId(droneId: string, file: File, meta: DroneResourceUpload): Promise<DroneResource> {
    const validationError = this.validate(file);
    if (validationError) {
      throw new Error(validationError);
    }

    const target = await this.getUploadTarget(droneId, file);
    // No x-amz-acl, which matches the hub signing drone resources private.
    await this.fileUploadService.uploadToSignedUrl(target.uploadUrl, file);

    const registered = await firstValueFrom(
      this.create(this.toRegistration(file, meta, target), { prefix: dronePrefix(droneId) })
    );
    if (!registered) {
      throw new Error('Upload saved to storage but could not be recorded.');
    }
    return registered;
  }

  async deleteResource(droneId: string, resourceId: string): Promise<void> {
    await firstValueFrom(this.delete(resourceId, { prefix: dronePrefix(droneId) }));
  }

  /** Null when acceptable, otherwise a user-facing reason. */
  validate(file: File): string | null {
    if (!this.fileUploadService.isAllowedFileType(file)) {
      return 'That file type isn\'t supported.';
    }
    if (file.size > this.fileUploadService.getMaxFileSize()) {
      return `File is too large (${this.fileUploadService.formatFileSize(file.size)}). Max is ${this.fileUploadService.formatFileSize(this.fileUploadService.getMaxFileSize())}.`;
    }
    return null;
  }

  /**
   * Resources are stored private, so a signed URL is the only way to open one.
   * It expires in an hour: fetch at click time rather than caching per row.
   */
  async getResourceDownloadUrl(droneId: string, resourceId: string): Promise<string> {
    const opts = { prefix: dronePrefix(droneId), id: resourceId, path: 'download-url' };
    const url = await firstValueFrom(this.request('GET', opts, data => (isRecord(data) ? nullableString(data['downloadUrl']) : null)));
    if (!url) {
      throw new Error('Could not open this document.');
    }
    return url;
  }

  private async getUploadTarget(droneId: string, file: File): Promise<DroneResourceUploadTarget> {
    const body: DroneResourceUploadUrlRequest = { filename: file.name, contentType: file.type || 'application/octet-stream' };
    const target = await firstValueFrom(
      this.request('POST', { prefix: dronePrefix(droneId), path: 'upload-url' }, toUploadTarget, body)
    );
    if (!target) {
      throw new Error('Could not prepare the upload.');
    }
    return target;
  }

  private toRegistration(file: File, meta: DroneResourceUpload, target: DroneResourceUploadTarget): DroneResourceRequest {
    return {
      resourceType: meta.resourceType,
      caption: meta.caption ?? null,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      fileExtension: this.fileUploadService.getFileExtension(file.name),
      s3Key: target.s3Key,
      cdnUrl: target.cdnUrl,
    };
  }
}

/** `drones/{droneId}`, with the id as its own segment. */
function dronePrefix(droneId: string): string[] {
  return ['drones', droneId];
}

function toUploadTarget(data: unknown): DroneResourceUploadTarget | null {
  const source = isRecord(data) ? data : {};
  const uploadUrl = nullableString(source['uploadUrl']);
  const s3Key = nullableString(source['s3Key']);
  const cdnUrl = nullableString(source['cdnUrl']);
  return uploadUrl && s3Key && cdnUrl ? { uploadUrl, s3Key, cdnUrl } : null;
}
